import { DoorKnock } from "@t/DoorKnock";
import { DoorKnockEventCreateDto } from "@t/DoorKnockEventCreateDto";
import { DoorKnockSaleCreateDto } from "@t/DoorKnockSaleCreateDto";
import { DoorKnockStatus } from "@t/DoorKnockStatus";
import { PhoneType } from "@t/PhoneType";
import { TargetDto } from "@t/TargetDto";
import { TargetQuality } from "@t/TargetQuality";
import { TargetStatus } from "@t/TargetStatus";
import { CalendarIcon, ClockIcon, DollarSignIcon, HomeIcon, LucideIcon, XIcon } from "lucide-react";

// Default appointment duration for schedule preview (in minutes)
export const DEFAULT_APPOINTMENT_DURATION_MINUTES = 60;

export interface MapStatusDisplay<T> {
    value: T;
    title: string;
    description?: string;
    icon?: LucideIcon;
    bgClassName?: string;
    textClassName?: string;
}

export const DK_STATUS_TEXT: Record<DoorKnockStatus, string> = {
    [DoorKnockStatus.NeverKnock]: "Never Knock",
    [DoorKnockStatus.NoAnswer]: "Not Home",
    [DoorKnockStatus.NotInterested]: "Not Interested",
    [DoorKnockStatus.GoBack]: "Call Back",
    [DoorKnockStatus.Proposed]: "Proposed Sale",
    [DoorKnockStatus.Sold]: "Sold",
};

export interface DoorKnockStatusColors {
    bg: string;
    hover: string;
    text: string;
    badgeText: string;
}

export const DK_STATUS_COLORS: Record<DoorKnockStatus, DoorKnockStatusColors> = {
    // [DoorKnockStatus.Untouched]: {
    //     bg: "bg-gray-500",
    //     hover: "hover:bg-gray-600",
    //     text: "text-gray-500",
    //     badgeText: "text-white",
    // },
    [DoorKnockStatus.NeverKnock]: {
        bg: "bg-red-500",
        hover: "hover:bg-red-600",
        text: "text-red-500",
        badgeText: "text-white",
    },
    [DoorKnockStatus.NoAnswer]: {
        bg: "bg-gray-500",
        hover: "hover:bg-gray-600",
        text: "text-gray-500",
        badgeText: "text-white",
    },
    [DoorKnockStatus.NotInterested]: {
        bg: "bg-red-500",
        hover: "hover:bg-red-600",
        text: "text-red-500",
        badgeText: "text-white",
    },
    [DoorKnockStatus.GoBack]: {
        bg: "bg-yellow-500",
        hover: "hover:bg-yellow-600",
        text: "text-yellow-500",
        badgeText: "text-white",
    },
    [DoorKnockStatus.Proposed]: {
        bg: "bg-purple-500",
        hover: "hover:bg-purple-600",
        text: "text-purple-500",
        badgeText: "text-white",
    },
    [DoorKnockStatus.Sold]: {
        bg: "bg-green-500",
        hover: "hover:bg-green-600",
        text: "text-green-500",
        badgeText: "text-white",
    },
};

export const DK_STATUS_DISPLAY: Record<DoorKnockStatus, MapStatusDisplay<DoorKnockStatus>> = {
    [DoorKnockStatus.NeverKnock]: {
        value: DoorKnockStatus.NeverKnock,
        title: DK_STATUS_TEXT[DoorKnockStatus.NeverKnock],
        bgClassName: DK_STATUS_COLORS[DoorKnockStatus.NeverKnock].bg,
    },
    [DoorKnockStatus.NoAnswer]: {
        value: DoorKnockStatus.NoAnswer,
        title: DK_STATUS_TEXT[DoorKnockStatus.NoAnswer],
        icon: HomeIcon,
        textClassName: DK_STATUS_COLORS[DoorKnockStatus.NoAnswer].text,
        bgClassName: DK_STATUS_COLORS[DoorKnockStatus.NoAnswer].bg,
    },
    [DoorKnockStatus.NotInterested]: {
        value: DoorKnockStatus.NotInterested,
        title: DK_STATUS_TEXT[DoorKnockStatus.NotInterested],
        icon: XIcon,
        textClassName: DK_STATUS_COLORS[DoorKnockStatus.NotInterested].text,
        bgClassName: DK_STATUS_COLORS[DoorKnockStatus.NotInterested].bg,
    },
    [DoorKnockStatus.GoBack]: {
        value: DoorKnockStatus.GoBack,
        title: DK_STATUS_TEXT[DoorKnockStatus.GoBack],
        icon: CalendarIcon,
        textClassName: DK_STATUS_COLORS[DoorKnockStatus.GoBack].text,
        bgClassName: DK_STATUS_COLORS[DoorKnockStatus.GoBack].bg,
    },
    [DoorKnockStatus.Proposed]: {
        value: DoorKnockStatus.Proposed,
        title: DK_STATUS_TEXT[DoorKnockStatus.Proposed],
        icon: DollarSignIcon,
        textClassName: DK_STATUS_COLORS[DoorKnockStatus.Proposed].text,
        bgClassName: DK_STATUS_COLORS[DoorKnockStatus.Proposed].bg,
    },
    [DoorKnockStatus.Sold]: {
        value: DoorKnockStatus.Sold,
        title: DK_STATUS_TEXT[DoorKnockStatus.Sold],
        icon: DollarSignIcon,
        textClassName: DK_STATUS_COLORS[DoorKnockStatus.Sold].text,
        bgClassName: DK_STATUS_COLORS[DoorKnockStatus.Sold].bg,
    },
};

/**
 * Colors for the "Processing Sale" status (Proposed with signed contract)
 */
export const DK_PROCESSING_SALE_COLORS: DoorKnockStatusColors = {
    bg: "bg-amber-500",
    hover: "hover:bg-amber-600",
    text: "text-white",
    badgeText: "text-white",
};

/**
 * Get the display status for a door knock event, accounting for signed contracts
 */
export function getDoorKnockDisplayStatus(event: {
    doorKnockResult: DoorKnockStatus;
    isContractSigned?: boolean;
}): {
    text: string;
    colors: DoorKnockStatusColors;
    icon?: LucideIcon;
} {
    // Special case: Proposed with signed contract = "Processing Sale"
    if (event.doorKnockResult === DoorKnockStatus.Proposed && event.isContractSigned === true) {
        return {
            text: "Processing Sale",
            colors: DK_PROCESSING_SALE_COLORS,
            icon: ClockIcon,
        };
    }

    // Default to standard status display
    return {
        text: DK_STATUS_TEXT[event.doorKnockResult],
        colors: DK_STATUS_COLORS[event.doorKnockResult],
        icon: DK_STATUS_DISPLAY[event.doorKnockResult]?.icon,
    };
}

export const BASE_DOORKNOCK: DoorKnock = {
    id: 0,
    oid: "",
    fullAddress: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    latitude: 0,
    longitude: 0,
    events: [],
    dateCreated: "",
    dateModified: "",
    isDeleted: false,
    companyId: 1,
};

export const BASE_TARGET: TargetDto = {
    oid: "",
    fullAddress: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    latitude: 0,
    longitude: 0,
    interactions: [],
    targetQuality: TargetQuality.Low,
    targetStatus: TargetStatus.Untouched,
};

export const BASE_DOORKNOCK_EVENT: DoorKnockEventCreateDto = {
    oid: "",
    doorKnockResult: DoorKnockStatus.NotInterested,
    note: "",
    firstName: "",
    lastName: "",
    fullAddress: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    latitude: 0,
    longitude: 0,
    emailAddress: "",
    emailDescription: "",
    phoneNumber: "",
    phoneExtension: "",
    phoneType: PhoneType.Cell,
    phoneDescription: "",
    phoneNumber2: "",
    phoneExtension2: "",
    phoneType2: PhoneType.Cell,
    phoneDescription2: "",
    userGeoLat: 0,
    userGeoLon: 0,
    officeId: 0,
};

export const BASE_DOORKNOCK_SALE: DoorKnockSaleCreateDto = {
    subscriptions: [],
    selectedTargetPestNames: [],
    hasCustomerConsentToSendSMS: false,
    fullAddress: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
    latitude: 0,
    longitude: 0,

    sendEmail: false,
    sendEmailToAddress: "",
    sendSms: false,
    sendSmsToPhoneNumber: "",
    sendQrCode: false,

    openOnDevice: false,

    overrideCheckPayment: false,
    overrideCreditCardPayment: false,
    overrideWiseTackPayment: false,

    contractId: null,
    chargePayment: false,
};
